// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Extension, Practitioner } from '@medplum/fhirtypes';
import { ext } from '../config/tenant';

// Extension URLs for user preferences
// All URLs are derived from tenant configuration for multi-tenancy support
export const USER_EXTENSION_URLS = {
  userPreferences: ext('user-preferences'),
} as const;

/**
 * Get an extension value from a Practitioner by URL
 */
export function getPractitionerExtension(
  practitioner: Practitioner | undefined,
  url: string
): Extension | undefined {
  return practitioner?.extension?.find((ext) => ext.url === url);
}

/**
 * Get a nested extension value from a complex extension
 */
export function getNestedExtensionValue<T>(
  extension: Extension | undefined,
  nestedUrl: string,
  valueKey: keyof Extension = 'valueString'
): T | undefined {
  const nested = extension?.extension?.find((ext) => ext.url === nestedUrl);
  return nested?.[valueKey] as T | undefined;
}

/**
 * Set or update an extension on a Practitioner
 * Returns a new Practitioner object with the updated extension
 */
export function setPractitionerExtension(
  practitioner: Practitioner,
  url: string,
  extension: Extension
): Practitioner {
  const existingExtensions = practitioner.extension?.filter((ext) => ext.url !== url) || [];

  return {
    ...practitioner,
    extension: [...existingExtensions, { ...extension, url }],
  };
}

// ============================================
// User Preferences Helpers
// ============================================

/** All possible appointment statuses */
export type AppointmentStatus = 'proposed' | 'pending' | 'booked' | 'arrived' | 'fulfilled' | 'cancelled' | 'noshow';

/** Default visible statuses for Kanban view */
export const DEFAULT_KANBAN_STATUSES: AppointmentStatus[] = ['booked', 'arrived', 'fulfilled', 'cancelled', 'noshow'];

export interface UserPreferencesData {
  /** Default view mode for appointments page */
  defaultAppointmentViewMode: 'list' | 'kanban';
  /** Which statuses to show in Kanban view */
  kanbanVisibleStatuses?: AppointmentStatus[];
  /** Default organization/practice ID to load on login */
  defaultOrganizationId?: string;
  /** User's preferred language (e.g., 'en', 'es', 'fr') */
  language?: string;
  /** User's preferred timezone (e.g., 'America/New_York'). If not set, uses practice timezone. */
  timezone?: string;
  /** App color scheme/theme ID (e.g., 'default', 'serene-sage', 'olive-oasis') */
  appThemeId?: string;
  /** App style ID ('default' or 'flat') */
  appStyleId?: string;
  /** Dark mode tint intensity ('enhanced', 'medium', 'gentle') */
  darkModeTintId?: string;
  /** Dark mode color base ('neutral' or 'blue') */
  darkModeBaseId?: string;
  /** Header theme color group ID */
  headerThemeColorGroupId?: string;
  /** Header theme color shade ('light', 'medium', 'dark') */
  headerThemeColorShade?: string;
  /** Header button color ID */
  headerButtonColorId?: string;
  /** Sidebar display mode ('full' or 'hover') */
  sidebarMode?: string;
}

/**
 * Get user preferences from a Practitioner resource
 */
export function getUserPreferences(practitioner: Practitioner | undefined): UserPreferencesData | undefined {
  const ext = getPractitionerExtension(practitioner, USER_EXTENSION_URLS.userPreferences);
  if (!ext?.extension) return undefined;

  // Parse kanbanVisibleStatuses from comma-separated string
  const kanbanStatusesStr = getNestedExtensionValue<string>(ext, 'kanbanVisibleStatuses', 'valueString');
  const kanbanVisibleStatuses = kanbanStatusesStr
    ? (kanbanStatusesStr.split(',') as AppointmentStatus[])
    : undefined;

  // Get default organization ID
  const defaultOrganizationId = getNestedExtensionValue<string>(ext, 'defaultOrganizationId', 'valueString');

  // Get language preference
  const language = getNestedExtensionValue<string>(ext, 'language', 'valueString');

  // Get timezone preference
  const timezone = getNestedExtensionValue<string>(ext, 'timezone', 'valueString');

  // Get display preferences
  const appThemeId = getNestedExtensionValue<string>(ext, 'appThemeId', 'valueString');
  const appStyleId = getNestedExtensionValue<string>(ext, 'appStyleId', 'valueString');
  const darkModeTintId = getNestedExtensionValue<string>(ext, 'darkModeTintId', 'valueString');
  const darkModeBaseId = getNestedExtensionValue<string>(ext, 'darkModeBaseId', 'valueString');
  const headerThemeColorGroupId = getNestedExtensionValue<string>(ext, 'headerThemeColorGroupId', 'valueString');
  const headerThemeColorShade = getNestedExtensionValue<string>(ext, 'headerThemeColorShade', 'valueString');
  const headerButtonColorId = getNestedExtensionValue<string>(ext, 'headerButtonColorId', 'valueString');
  const sidebarMode = getNestedExtensionValue<string>(ext, 'sidebarMode', 'valueString');

  return {
    defaultAppointmentViewMode:
      (getNestedExtensionValue<string>(ext, 'defaultAppointmentViewMode', 'valueString') as 'list' | 'kanban') || 'list',
    kanbanVisibleStatuses,
    defaultOrganizationId,
    language,
    timezone,
    appThemeId,
    appStyleId,
    darkModeTintId,
    darkModeBaseId,
    headerThemeColorGroupId,
    headerThemeColorShade,
    headerButtonColorId,
    sidebarMode,
  };
}

/**
 * Build user preferences extension for FHIR storage
 */
export function buildUserPreferencesExtension(preferences: Partial<UserPreferencesData>): Extension {
  const extensions: Extension[] = [];

  if (preferences.defaultAppointmentViewMode) {
    extensions.push({ url: 'defaultAppointmentViewMode', valueString: preferences.defaultAppointmentViewMode });
  }

  if (preferences.kanbanVisibleStatuses) {
    extensions.push({ url: 'kanbanVisibleStatuses', valueString: preferences.kanbanVisibleStatuses.join(',') });
  }

  if (preferences.defaultOrganizationId) {
    extensions.push({ url: 'defaultOrganizationId', valueString: preferences.defaultOrganizationId });
  }

  if (preferences.language) {
    extensions.push({ url: 'language', valueString: preferences.language });
  }

  if (preferences.timezone) {
    extensions.push({ url: 'timezone', valueString: preferences.timezone });
  }

  // Display preferences
  if (preferences.appThemeId) {
    extensions.push({ url: 'appThemeId', valueString: preferences.appThemeId });
  }

  if (preferences.appStyleId) {
    extensions.push({ url: 'appStyleId', valueString: preferences.appStyleId });
  }

  if (preferences.darkModeTintId) {
    extensions.push({ url: 'darkModeTintId', valueString: preferences.darkModeTintId });
  }

  if (preferences.darkModeBaseId) {
    extensions.push({ url: 'darkModeBaseId', valueString: preferences.darkModeBaseId });
  }

  if (preferences.headerThemeColorGroupId) {
    extensions.push({ url: 'headerThemeColorGroupId', valueString: preferences.headerThemeColorGroupId });
  }

  if (preferences.headerThemeColorShade) {
    extensions.push({ url: 'headerThemeColorShade', valueString: preferences.headerThemeColorShade });
  }

  if (preferences.headerButtonColorId) {
    extensions.push({ url: 'headerButtonColorId', valueString: preferences.headerButtonColorId });
  }

  if (preferences.sidebarMode) {
    extensions.push({ url: 'sidebarMode', valueString: preferences.sidebarMode });
  }

  return {
    url: USER_EXTENSION_URLS.userPreferences,
    extension: extensions,
  };
}

/**
 * Merge new preferences with existing preferences
 */
export function mergeUserPreferences(
  existing: UserPreferencesData | undefined,
  updates: Partial<UserPreferencesData>
): UserPreferencesData {
  return {
    defaultAppointmentViewMode: updates.defaultAppointmentViewMode ?? existing?.defaultAppointmentViewMode ?? 'list',
    kanbanVisibleStatuses: updates.kanbanVisibleStatuses ?? existing?.kanbanVisibleStatuses,
    defaultOrganizationId: updates.defaultOrganizationId ?? existing?.defaultOrganizationId,
    language: updates.language ?? existing?.language,
    timezone: updates.timezone ?? existing?.timezone,
    appThemeId: updates.appThemeId ?? existing?.appThemeId,
    appStyleId: updates.appStyleId ?? existing?.appStyleId,
    darkModeTintId: updates.darkModeTintId ?? existing?.darkModeTintId,
    darkModeBaseId: updates.darkModeBaseId ?? existing?.darkModeBaseId,
    headerThemeColorGroupId: updates.headerThemeColorGroupId ?? existing?.headerThemeColorGroupId,
    headerThemeColorShade: updates.headerThemeColorShade ?? existing?.headerThemeColorShade,
    headerButtonColorId: updates.headerButtonColorId ?? existing?.headerButtonColorId,
    sidebarMode: updates.sidebarMode ?? existing?.sidebarMode,
  };
}
